<!-- 退款处理的统计、列表与分页展示。审核弹窗和数据请求由父页面处理。 -->
<template>
  <section class="refund-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <div class="filter-bar__meta">
          <span>当前仅接后端分页与审核链路，筛选条件待后端补充后继续扩展。</span>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>退款单号</th>
              <th>订单号</th>
              <th>用户 ID</th>
              <th>退款原因</th>
              <th>退款渠道</th>
              <th>退款状态</th>
              <th class="is-right">退款金额</th>
              <th>申请时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in refunds" :key="row.id">
              <td><span class="cell-text">{{ row.refundNo }}</span></td>
              <td><span class="cell-text">{{ row.orderNo || row.orderId }}</span></td>
              <td><span class="cell-text">{{ row.userId }}</span></td>
              <td><span class="cell-text">{{ row.reason || '--' }}</span></td>
              <td><span class="cell-text">{{ getRefundChannelText(row.channel) }}</span></td>
              <td><span :class="['status-tag', toneClass(refundStatusTone(row.status))]">{{ getRefundStatusText(row.status) }}</span></td>
              <td class="is-right">{{ formatAmount(row.amount) }}</td>
              <td>{{ row.createdAt }}</td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="审核退款" @click="emit('openRefund', row)"><el-icon><View /></el-icon></button>
                  <button
                    v-if="canRefreshWechatRefund(row)"
                    title="刷新微信退款状态"
                    :disabled="refreshingRefundId === row.id"
                    @click="emit('refreshWechatStatus', row)"
                  >
                    <el-icon><Refresh /></el-icon>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!refunds.length && !loading"><td colspan="9"><el-empty description="暂无退款数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="page" class="table-footer">
        <span>共 {{ page.total }} 条记录，第 {{ page.page }}/{{ totalPages }} 页</span>
        <div>
          <button :disabled="page.page <= 1" @click="emit('changePage', page.page - 1)">上一页</button>
          <button class="is-active">{{ page.page }}</button>
          <button :disabled="!page.hasMore" @click="emit('changePage', page.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import { Refresh, View } from '@element-plus/icons-vue'
import type { PageResult } from '@jiaofa/api-types'
import { RefundStatus } from '@jiaofa/constants'
import type { AdminRefund } from '@/api/order'
import type { OperationStat } from '@/data/operationDesign'

type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  stats: OperationStat[]
  refunds: AdminRefund[]
  page?: PageResult<AdminRefund>
  loading: boolean
  refreshingRefundId: string
  totalPages: number
  formatAmount: (amountInFen: number) => string
  getRefundStatusText: (status: RefundStatus) => string
  getRefundChannelText: (channel?: string) => string
  refundStatusTone: (status: RefundStatus) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  openRefund: [refund: AdminRefund]
  refreshWechatStatus: [refund: AdminRefund]
  changePage: [page: number]
}>()

const {
  stats,
  refunds,
  page,
  loading,
  refreshingRefundId,
  totalPages,
  formatAmount,
  getRefundStatusText,
  getRefundChannelText,
  refundStatusTone,
  toneClass,
} = toRefs(props)

function canRefreshWechatRefund(refund: AdminRefund): boolean {
  return refund.channel === 'WECHAT' && refund.status === RefundStatus.Refunding
}
</script>

<style scoped lang="scss">
.refund-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-bar__meta { flex: 1; color: var(--jf-color-text-secondary); font-size: 13px; }
.filter-bar__actions { display: flex; gap: 8px; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button { height: 36px; padding: 0 16px; border-radius: 8px; background: var(--jf-color-primary); color: #fff; font-size: 14px; font-weight: 700; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.row-actions button:disabled, .table-footer button:disabled { cursor: not-allowed; opacity: .5; }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
